// Recipient magic-link landing — /r/:token. Claims the passwordless recipient
// account behind the token, logs them in, and drops them in their portal.

import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Loader2, AlertTriangle } from 'lucide-react';
import { recipientClaim } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

export function RecipientClaim() {
  const { token } = useParams<{ token: string }>();
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;     // claim once (and not twice under StrictMode)
    ran.current = true;
    (async () => {
      try {
        await recipientClaim(token ?? '');
        await refresh();
        navigate('/recipient', { replace: true });
      } catch (e) {
        setError((e as Error).message || 'This link is invalid or has expired.');
      }
    })();
  }, [token, refresh, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--color-panel)] px-4">
      <div className="w-full max-w-sm bg-white border border-[var(--color-border)] rounded-2xl shadow-sm p-8 text-center">
        <div className="text-[22px] font-bold text-brand-700 mb-1">Lakay<span className="text-[var(--color-warning-fg)]">Cart</span></div>
        {error ? (
          <>
            <AlertTriangle className="w-8 h-8 text-[var(--color-warning-fg)] mx-auto mt-4" />
            <p className="mt-3 text-[14px] font-semibold text-[var(--color-ink)]">Couldn't open your link</p>
            <p className="mt-1 text-[12.5px] text-[var(--color-secondary-2)]">{error}</p>
            <Link to="/login" className="mt-4 inline-block text-[13px] font-semibold text-brand-600 hover:underline">Go to sign in</Link>
          </>
        ) : (
          <>
            <Loader2 className="w-7 h-7 text-brand-500 animate-spin mx-auto mt-5" />
            <p className="mt-3 text-[13.5px] text-[var(--color-secondary)]">Signing you in…</p>
          </>
        )}
      </div>
    </div>
  );
}
